import { Link, useParams } from "react-router-dom";
import { Seo } from "../components/Seo";
import { MessageCircle, MapPin } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { PA_BASE, waLink } from "../config";
import { KineticHero } from "../components/KineticHero";
import { Reveal, Overline } from "../components/motion";
import { DESTINATIONS } from "../data/destinations";
import { ROUTES } from "../data/routes";

export default function DestinationCityPage() {
  const { city } = useParams();
  const d = DESTINATIONS.find((x) => x.slug === city);

  if (!d) {
    return (
      <section className="bg-[#F9F9F6] px-5 py-40 sm:px-8" data-testid="destination-not-found">
        <div className="mx-auto max-w-[800px]">
          <h1 className="font-display text-4xl tracking-tight">Destination not found.</h1>
          <Link to={`${PA_BASE}/destinations`} className="mt-6 inline-block text-sm underline underline-offset-4">
            View all destinations
          </Link>
        </div>
      </section>
    );
  }

  const relatedRoutes = ROUTES.filter((r) => r.fromCity === d.slug || r.toCity === d.slug);

  return (
    <>
      <Seo
        title={`Private Jet Charter to ${d.name} | JS Private Aviation`}
        description={`Private charter to ${d.name}, ${d.country} — executive terminals, airports, popular routes and FAQs. Arranged through independent aircraft operators.`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: d.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />
      <KineticHero
        testId={`destination-hero-${d.slug}`}
        fullHeight={false}
        overline={`Destination — ${d.country}`}
        lines={["Private charter", `to ${d.name}.`]}
        sub={d.intro}
        image={d.image}
      >
        <Link
          to={`${PA_BASE}/charter?destination=${encodeURIComponent(d.name)}#request`}
          data-testid="destination-quote-cta"
          className="inline-flex items-center gap-3 bg-white px-8 py-4 text-[12px] uppercase tracking-[0.22em] text-black transition-colors duration-300 hover:bg-white/85"
        >
          Request a Quote
        </Link>
        <a
          href={waLink(`Hello — I would like to charter to ${d.name}.`)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="destination-whatsapp-cta"
          className="inline-flex items-center gap-2 border border-white/35 px-8 py-4 text-[12px] uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
        >
          <MessageCircle size={15} /> WhatsApp a Specialist
        </a>
      </KineticHero>

      <section className="bg-[#F9F9F6] py-24 sm:py-32">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-16 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <Overline>Airports</Overline>
            <h2 className="mt-3 font-display text-3xl tracking-tight">Where you arrive.</h2>
            <ul className="mt-8 space-y-5">
              {d.airports.map((a) => (
                <li key={a.name} className="flex gap-4 border-b border-black/10 pb-5" data-testid={`airport-${a.name.slice(0, 12).toLowerCase().replace(/\s+/g, "-")}`}>
                  <MapPin size={16} className="mt-1 shrink-0 text-black/35" />
                  <div>
                    <p className="text-[15px] font-medium text-black/85">{a.name}</p>
                    <p className="mt-1 text-sm text-black/50">{a.note}</p>
                  </div>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <Overline>Why charter here</Overline>
            <h2 className="mt-3 font-display text-3xl tracking-tight">The case for private.</h2>
            <ul className="mt-8 space-y-5">
              {d.why.map((w) => (
                <li key={w} className="flex items-baseline gap-4 border-b border-black/10 pb-5 text-[15px] text-black/70">
                  <span className="font-mono text-[10px] text-black/30">—</span>
                  {w}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        {relatedRoutes.length > 0 && (
          <div className="mx-auto mt-24 max-w-[1400px] px-5 sm:px-8">
            <Reveal>
              <Overline>Popular routes</Overline>
              <h2 className="mt-3 font-display text-3xl tracking-tight">Routes involving {d.name}.</h2>
              <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {relatedRoutes.map((r) => (
                  <Link
                    key={r.slug}
                    to={`${PA_BASE}/routes/${r.slug}`}
                    data-testid={`related-route-${r.slug}`}
                    className="group flex items-center justify-between border border-black/10 bg-white p-7 transition-shadow duration-300 hover:shadow-[0_16px_40px_rgba(0,0,0,0.07)]"
                  >
                    <span className="font-display text-xl tracking-tight">
                      {r.from} <span className="text-black/30">→</span> {r.to}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/40 transition-colors duration-300 group-hover:text-black">
                      View route
                    </span>
                  </Link>
                ))}
              </div>
            </Reveal>
          </div>
        )}

        <div className="mx-auto mt-24 max-w-[900px] px-5 sm:px-8">
          <Reveal>
            <Overline>Questions</Overline>
            <h2 className="mt-3 font-display text-3xl tracking-tight">{d.name}, answered.</h2>
            <Accordion type="single" collapsible className="mt-10" data-testid="destination-faq">
              {d.faqs.map((f, i) => (
                <AccordionItem key={i} value={`f${i}`} className="border-black/10">
                  <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left text-[15px] font-medium hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-black/60">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </>
  );
}
